import React, { 
    useState, 
    JSX,
} from "react"

import DGAmplify, {
    DGAmplifyTools,
} from "../Databases/DGAmplify.m"

import {
    Assessed,
    Assessment,
    AssessmentTask,
    AssessmentTaskPerformanceStep,
    PerformanceStep,
    Task,
    QuarterlyAssessmentDetail,
    AssessmentDetail,
    SubTask,
    Site,
    Unit,
    User as EMETLUser,
} from "../../API"

import PAGES, {
    EMETLPage,
} from "../EMETLPages.m"

import {
    EMETLAssessmentTaskWithDetails,
    EMETLStaticTaskStructure,
} from "../EMETLAssessment.m"

export const Context = React.createContext(undefined)

const {
    Provider,
    Consumer,
} = Context

type ContextProps = {
    children: React.ReactNode
}

export type EMETLContext = {
    amplify: DGAmplifyTools,
    mode: "light" | "dark",
    header: {
        update: (a: JSX.Element) => void,
        value: JSX.Element,
    },
    banner: {
        info: string,
        setInfo: (s: string) => void,
        warning: string,
        setWarning: (s: string) => void,
        error: string,
        setError: (s: string) => void,
    },
    assessmentData?: {
        assessed?: {
            value: Assessed[],
            update: (a: Assessed[]) => void,
        }
        assessments?: {
            value: Assessment[],
            update: (a: Assessment[]) => void,
        },
        selectedAssessmentIndex?: {
            value: number | null,
            update: (a: number | null) => void,
        },
        selectedAssessmentTaskIndex?: {
            value: number | null,
            update: (a: number | null) => void,
        },
        selectedATPSIndex?: {
            value: number | null,
            update: (a: number | null) => void,
        },
        assessmentDetail?: {
            value: AssessmentDetail,
            update: (a: AssessmentDetail) => void,
        },
        assessmentTasks?: {
            value: EMETLAssessmentTaskWithDetails[],
            update: (a: EMETLAssessmentTaskWithDetails[]) => void,
        },
        assessmentTaskPerformanceSteps?: {
            value: AssessmentTaskPerformanceStep[],
            update: (a: AssessmentTaskPerformanceStep[]) => void,
        },
        quarterlyAssessmentDetail?: {
            value: QuarterlyAssessmentDetail,
            update: (a: QuarterlyAssessmentDetail) => void,
        },
        unitID?: {
            value: number,
            update: (a: number) => void,
        },
        subtasks?: {
            value: SubTask[],
            update: (a: SubTask[]) => void,
        },
    },
    modal: {
        content: React.ReactNode,
        setContent: (c: React.ReactNode) => void,
        title: string,
        setTitle: (t: string) => void,
    },
    sites?: Site[],
    selectedSiteIDs?: number[],
    staticTasksAndPS?: {
        value: EMETLStaticTaskStructure,
        update: (tasksAndPS: EMETLStaticTaskStructure) => void,
    },
    loading: {
        update: (l: boolean) => void,
        value: boolean,
        message: string,
        setMessage: (m: string) => void,
    },
    isSiteAdmin?: boolean,
    rank?: string,
    pages?: {
        value: EMETLPage[],
        update: (pages: EMETLPage[]) => void,
    },
    units?: {
        value: Unit[],
        update: (units: Unit[]) => void,
    },
    personnel?: {
        value: EMETLUser[],
        update: (personnel: EMETLUser[]) => void,
    },
    viewName?: {
        value: string,
        update: (viewName: string) => void,
    }
}

export const AppContextProvider = (props: ContextProps) => {
    const {
        children,
    } = props

    const [assessed, setAssessed] = useState<Assessed[]>([])
    const [assessments, setAssessments] = useState<Assessment[]>([])
    const [selectedAssessmentIndex, setSelectedAssessmentIndex] = useState<number | null>(null)
    const [selectedAssessmentTaskIndex, setSelectedAssessmentTaskIndex] = useState<number | null>(null)
    const [selectedATPSIndex, setSelectedATPSIndex] = useState<number | null>(null)
    const [assessmentDetail, setAssessmentDetail] = useState<AssessmentDetail>()
    const [assessmentTasks, setAssessmentTasks] = useState<EMETLAssessmentTaskWithDetails[]>([])
    const [assessmentTaskPerformanceSteps, setAssessmentTaskPerformanceSteps] = useState<AssessmentTaskPerformanceStep[]>([])
    const [quarterlyAssessmentDetail, setQuarterlyAssessmentDetail] = useState<QuarterlyAssessmentDetail>()
    const [unitID, setUnitID] = useState<number>()
    const [subtasks, setSubtasks] = useState<SubTask[]>([])
    const [mode, _setMode] = useState<"light" | "dark">("light")
    const [isSiteAdmin, _setIsSiteAdmin] = useState<boolean>(false)
    const [rank, _setRank] = useState<string>()
    const [loading, setLoading] = useState<boolean>(true)
    const [header, setHeader] = useState<JSX.Element>(null)
    const [viewName, setViewName] = useState<string>("")
    const [modalContent, setModalContent] = useState<React.ReactNode>(null)
    const [modalTitle, setModalTitle] = useState<string>("")
    const [message, setMessage] = useState<string>("")
    const [pages, setPages] = useState<EMETLPage[]>(PAGES)
    const [staticTasksAndPs, setStaticTasksAndPS] = useState<EMETLStaticTaskStructure>({})
    const [units, setUnits] = useState<Unit[]>([]) 
    const [info, setInfo] = useState<string>("")
    const [warning, setWarning] = useState<string>("")
    const [error, setError] = useState<string>("")
    const [personnel, setPersonnel] = useState<EMETLUser[]>([])
    /**
     * Handle changes in the authenticated user's state
     * 
     * @param {AuthState} nextAuthState The next authentication state
     * @param {CognitoUserInterface} authData The authenticated user's data
     */
    // const handler: AuthStateHandler = (nextAuthState: AuthState, authData: User) => {
    //     setUser(authData)
    // }

    // useEffect(() => {
    //     // lock scrolling when modal is open
    //     if (modalContent) {
    //         document.body.style.overflow = "hidden"
    //     } else {
    //         document.body.style.overflow = "auto"
    //     }
    //     return () => {
    //         document.body.style.overflow = "auto"
    //     }
    // }, [modalContent])

    const context: EMETLContext = {
        rank,
        amplify: DGAmplify,
        mode,
        loading: {
            update: setLoading,
            value: loading,
            message,
            setMessage,
        },
        isSiteAdmin,
        header: {
            update: setHeader,
            value: header,
        },
        modal: {
            content: modalContent,
            setContent: setModalContent,
            title: modalTitle,
            setTitle: setModalTitle,
        },
        banner: {
            info,
            setInfo,
            warning,
            setWarning,
            error,
            setError,
        },
        assessmentData: {
            assessed: {
                value: assessed,
                update: setAssessed,
            },
            assessments: {
                value: assessments,
                update: setAssessments,
            },
            selectedAssessmentIndex: {
                value: selectedAssessmentIndex,
                update: setSelectedAssessmentIndex,
            },
            selectedAssessmentTaskIndex: {
                value: selectedAssessmentTaskIndex,
                update: setSelectedAssessmentTaskIndex,
            },
            selectedATPSIndex: {
                value: selectedATPSIndex,
                update: setSelectedATPSIndex,
            },
            assessmentDetail: {
                value: assessmentDetail,
                update: setAssessmentDetail,
            },
            assessmentTasks: {
                value: assessmentTasks,
                update: setAssessmentTasks,
            },
            assessmentTaskPerformanceSteps: {
                value: assessmentTaskPerformanceSteps,
                update: setAssessmentTaskPerformanceSteps,
            },
            quarterlyAssessmentDetail: {
                value: quarterlyAssessmentDetail,
                update: setQuarterlyAssessmentDetail,
            },
            unitID: {
                value: unitID,
                update: setUnitID,
            },
            subtasks: {
                value: subtasks,
                update: setSubtasks,
            },
        },
        pages: {
            value: pages,
            update: setPages,
        },
        staticTasksAndPS: {
            value: staticTasksAndPs,
            update: setStaticTasksAndPS,
        },
        units: {
            value: units,
            update: setUnits,
        },
        personnel: {
            value: personnel,
            update: setPersonnel,
        },
        viewName: {
            value: viewName,
            update: setViewName,
        }
    }

    return (
        <Provider 
            value={context}
        >
            <>
                {children}
            </>
        </Provider>
    )
}

export default Consumer